import React, { useState, useRef, useEffect } from "react";
import { Button, Input, Spin } from "antd";
import { MessageOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import axios from "axios";
import styles from "./Chatbot.module.css";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm your ERP assistant. Ask me about products, customers, suppliers, sales, purchases, or stock. Type 'help' for supported queries.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    const text = (input || "").trim();
    if (!text || loading) return;

    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      // Send conversation history for LLM context (exclude initial greeting, include all prior messages)
      const history = messages
        .filter((m) => m.role && m.content && !m.error)
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await axios.post("chatbot/query", {
        message: text,
        history,
      });
      const reply = data?.reply || "Sorry, I couldn't process that.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      const msg =
        err?.response?.data?.reply ||
        err?.response?.data?.message ||
        "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: msg, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content) => {
    if (!content) return "";
    return content
      .split("\n")
      .map((line, i) => {
        if (line.startsWith("**") && line.endsWith("**")) {
          return (
            <strong key={i}>
              {line.slice(2, -2)}
              <br />
            </strong>
          );
        }
        if (line.startsWith("• ")) {
          return (
            <span key={i} className={styles.bullet}>
              {line}
              <br />
            </span>
          );
        }
        return (
          <span key={i}>
            {line}
            <br />
          </span>
        );
      });
  };

  return (
    <div className="erp-no-print">
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={open ? <CloseOutlined /> : <MessageOutlined />}
        className={styles.fab}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chatbot" : "Open chatbot"}
      />
      {open && (
        <div className={styles.panel}>
          <div className={styles.header}>
            <span className={styles.title}>ERP Assistant</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
              className={styles.closeBtn}
            />
          </div>
          <div className={styles.messages}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === "user"
                    ? styles.userMsg
                    : m.error
                    ? styles.errorMsg
                    : styles.assistantMsg
                }
              >
                {formatMessage(m.content)}
              </div>
            ))}
            {loading && (
              <div className={styles.assistantMsg}>
                <Spin size="small" /> Thinking...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className={styles.inputArea}>
            <Input
              placeholder="Ask about products, sales, customers..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              suffix={
                <Button
                  type="link"
                  icon={<SendOutlined />}
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                />
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
